import { Injectable } from '@angular/core';
import { WorkspaceRepository } from '@database/central';
import { TableRepository, WorkspaceDatabaseKeys } from '@database/workspace';
import { catchError, combineLatest, first, map, Observable, of } from 'rxjs';
import { ErrorCodes, TableModel, TableTypeEnum, ToastDuration, ToastService } from '@shared';
import { LoggingService } from '@shared/logging';
import { QueryParamsProvider } from '../../routing/services/query-params.provider';
import { StandardTableProvider } from '../standards/standard-table.provider';

@Injectable()
export class TableTypeProvider {
	public constructor(
		private queryParamsProvider: QueryParamsProvider,
		private workspaceRepository: WorkspaceRepository,
		private tableRepository: TableRepository,
		private standardTableProvider: StandardTableProvider,
		private loggerService: LoggingService,
		private toastService: ToastService
	) {}

	public isChildTable(): boolean {
		return !!this.queryParamsProvider.childTableName;
	}

	public isChildTable$(workfileId: string, table: string): Observable<boolean> {
		return this.isParentTable$(workfileId, table).pipe(map((isParentTable: boolean) => !isParentTable));
	}

	public isParentTable$(workfileId: string, table: string): Observable<boolean> {
		return this.workspaceRepository.isParentEntity$(workfileId, table);
	}

	public isSampleTable(tableName: string): boolean {
		return WorkspaceDatabaseKeys.tables.sampleTables.some((table: string) =>
			tableName.toLowerCase().startsWith(table)
		);
	}

	public isSampleTable$(tableName: string): Observable<boolean> {
		return tableName
			? combineLatest([
					of(this.isSampleTable(tableName)),
					this.isFlatSamplesTable$(
						this.queryParamsProvider.workfileId,
						this.queryParamsProvider.activeTableName
					)
			  ]).pipe(
					first(),
					map(([isSampleTable, isFlatSamplesTable]) => isSampleTable || isFlatSamplesTable)
			  )
			: of(false);
	}

	public isStandardTable$(tableName: string): Observable<boolean> {
		return tableName ? of(tableName === this.standardTableProvider.standardTable) : of(false);
	}

	public isFlatSamplesTable$(workfileId: string, table: string): Observable<boolean> {
		return combineLatest([
			this.isParentTable$(workfileId, table),
			this.tableRepository.tableHasChildren$(table),
			this.tableRepository.getStandardTable$(table)
		]).pipe(map(([isParent, hasChildren, standardTable]) => isParent && !hasChildren && !!standardTable));
	}

	public isFlatTable$(workfileId: string, table: string): Observable<boolean> {
		return combineLatest([
			this.isParentTable$(workfileId, table),
			this.tableRepository.tableHasChildren$(table),
			this.tableRepository
				.getTableMetadata$(table)
				.pipe(map((tableMetadata: TableModel) => !!tableMetadata.standard_table))
		]).pipe(
			map(
				(tableResult: [isParent: boolean, hasChildren: boolean, hasStandard: boolean]) =>
					tableResult[0] && !tableResult[1] && !tableResult[2]
			),
			catchError((error) =>
				this.toastService.presentError(`${error}: Flat table check failed`, ToastDuration.Slow)
			)
		);
	}

	// todo: do we need this one?
	public isFlatStandardTable$(workfileId: string, table: string, childTable: string): Observable<boolean> {
		return combineLatest([this.isFlatSamplesTable$(workfileId, table), this.isStandardTable$(childTable)]).pipe(
			map(([isFlat, isStandard]) => isFlat && isStandard)
		);
	}

	public getTableType$(tableName: string, workfileId: string): Observable<string[]> {
		if (!tableName) {
			this.loggerService.error(
				ErrorCodes.NullInputParam,
				'No table name provided as param for getting table type'
			);
			throw new Error('No table name or workfile id provided as param');
		}

		if (!workfileId) {
			this.loggerService.error(
				ErrorCodes.NullInputParam,
				'No workfile id provided as param for getting table type'
			);
			throw new Error('No table name or workfile id provided as param');
		}

		return combineLatest([
			this.isChildTable$(workfileId, tableName).pipe(
				map((isChildTable: boolean) => (isChildTable ? TableTypeEnum.childTable : null))
			),
			this.isParentTable$(workfileId, tableName).pipe(
				map((isParentTable: boolean) => (isParentTable ? TableTypeEnum.parentTable : null))
			),
			this.isSampleTable$(tableName).pipe(
				map((isSampleTable: boolean) => (isSampleTable ? TableTypeEnum.sampleTable : null))
			),
			this.isStandardTable$(tableName).pipe(
				map((isStandardTable: boolean) => (isStandardTable ? TableTypeEnum.standardTable : null))
			),
			this.isFlatSamplesTable$(workfileId, tableName).pipe(
				map((isFlatSamplesTable: boolean) => (isFlatSamplesTable ? TableTypeEnum.flatSampleTable : null))
			),
			this.isFlatTable$(workfileId, tableName).pipe(
				map((isFlatTable: boolean) => (isFlatTable ? TableTypeEnum.flatTable : null))
			)
		]).pipe(
			map(
				(
					matchingTableTypes: [
						TableTypeEnum,
						TableTypeEnum,
						TableTypeEnum,
						TableTypeEnum,
						TableTypeEnum,
						TableTypeEnum
					]
				) => {
					const tableTypes = [];

					for (const tableType of matchingTableTypes) {
						if (tableType) {
							tableTypes.push(tableType);
						}
					}

					return tableTypes;
				}
			)
		);
	}
}
